import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { selectUser } from "../../../features/userSlice";
import { tax as leadStates } from "../../../components/Forms/SelectValues/values";
import { submitRemodelLead } from "../../../components/Forms/Remodel/api";
import { PageHeader, PageShell } from "../../../components/UI/Page";

// Mirror the server's allowed list (remodelLeadPdf.js INTEREST_OPTIONS).
const INTEREST_OPTIONS = ["Windows", "Entry Doors", "Patio Doors", "Other"];

// Autosave key — a reload or an accidental back should not cost the lead.
const DRAFT_KEY = "remodelFormDraft";

const EMPTY = {
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  preferred_contact: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  salesperson_name: "",
  salesperson_email: "",
  interested_in: [],
  notes: "",
  concerns: "",
  built_prior_1978: "",
};

const Field = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium">
      {label}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </p>
    {children}
    {hint && <p className="app-muted text-xs">{hint}</p>}
  </div>
);

// One titled card per group of fields.
const Section = ({ title, required, description, children }) => (
  <section className="app-card rounded-2xl border p-5 sm:p-6">
    <div className="mb-4">
      <h2 className="text-base font-semibold">
        {title}
        {required && (
          <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
        )}
      </h2>
      {description && (
        <p className="app-muted mt-0.5 text-sm">{description}</p>
      )}
    </div>
    {children}
  </section>
);

const Choice = ({ checked, onChange, label, name }) => (
  <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-sm">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 cursor-pointer accent-sky-600"
    />
    <span>{label}</span>
  </label>
);

const Check = ({ checked, onChange, label }) => (
  <label className="app-surface flex min-h-[2.75rem] cursor-pointer items-center gap-2.5 rounded-xl border px-3 text-sm">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 cursor-pointer accent-sky-600"
    />
    <span>{label}</span>
  </label>
);

const RemodelForm = () => {
  const user = useSelector(selectUser);

  const [values, setValues] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { id }
  const [error, setError] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Restore a draft first, then fill the submitter's own details into
  // whatever is still blank.
  useEffect(() => {
    let restored = null;
    try {
      const saved = window.localStorage.getItem(DRAFT_KEY);
      if (saved) {
        restored = JSON.parse(saved);
        setDraftRestored(true);
      }
    } catch (err) {
      // Corrupt or unavailable storage — just start clean.
    }
    setValues((prev) => ({
      ...prev,
      ...(restored || {}),
      salesperson_name: restored?.salesperson_name || user?.name || "",
      salesperson_email: restored?.salesperson_email || user?.email || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.email]);

  useEffect(() => {
    if (submitted) return;
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
    } catch (err) {
      // Storage full or blocked; autosave is a convenience, not a promise.
    }
  }, [values, submitted]);

  const set = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }));

  const setValue = (field, value) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const allInterests = values.interested_in.length === INTEREST_OPTIONS.length;

  const toggleInterest = (option) =>
    setValues((prev) => ({
      ...prev,
      interested_in: prev.interested_in.includes(option)
        ? prev.interested_in.filter((item) => item !== option)
        : [...prev.interested_in, option],
    }));

  const toggleAllInterests = () =>
    setValues((prev) => ({
      ...prev,
      interested_in:
        prev.interested_in.length === INTEREST_OPTIONS.length
          ? []
          : [...INTEREST_OPTIONS],
    }));

  const missing = useMemo(() => {
    const need = {
      address: "Street Address",
      city: "City",
      state: "State",
      zip: "ZIP Code",
      salesperson_name: "Salesperson Name",
      salesperson_email: "Salesperson Email",
    };
    return Object.entries(need)
      .filter(([field]) => !String(values[field] || "").trim())
      .map(([, label]) => label);
  }, [values]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (missing.length) {
      setError(`Still needed: ${missing.join(", ")}.`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitRemodelLead(values, user);
      try {
        window.localStorage.removeItem(DRAFT_KEY);
      } catch (err) {
        // Nothing to do; the draft will be overwritten by the next form.
      }
      setSubmitted(result);
    } catch (err) {
      setError(err.message || "Could not submit the lead — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setValues({
      ...EMPTY,
      salesperson_name: user?.name || "",
      salesperson_email: user?.email || "",
    });
    setSubmitted(null);
    setDraftRestored(false);
  };

  if (submitted) {
    return (
      <PageShell size="sm" className="sm:py-12">
        <div className="app-card flex flex-col items-center rounded-2xl border px-6 py-10 text-center sm:px-10">
          <span className="app-icon-tile h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <TaskAltIcon fontSize="large" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            Remodel lead submitted
          </h1>
          <p className="app-muted mt-2 max-w-md text-sm leading-relaxed">
            The remodel team has been emailed the lead with the printable form
            attached, and a copy went to your inbox. You can find it again
            under My Submissions.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              component={Link}
              to="/forms/my-submissions"
              variant="contained"
            >
              My Submissions
            </Button>
            <Button variant="outlined" onClick={startAnother}>
              Submit Another
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell size="md">
      <PageHeader
        icon={HomeWorkIcon}
        tone="orange"
        eyebrow="Forms"
        title="Remodel Form"
        description="Send a remodel lead to the remodel team. Your answers are kept in this browser as you type, so you won't lose them."
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {draftRestored && (
          <div
            role="status"
            className="rounded-xl bg-sky-500/10 px-4 py-3 text-sm text-sky-800 dark:text-sky-200"
          >
            Picked up where you left off. Use "Clear form" below to start
            over.
          </div>
        )}

        {/* Customer */}
        <Section title="Customer" description="Whatever contact details you have.">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Name">
              <TextField
                fullWidth
                size="small"
                value={values.customer_name}
                onChange={set("customer_name")}
                inputProps={{ "aria-label": "Name" }}
              />
            </Field>
            <Field label="Phone">
              <TextField
                fullWidth
                size="small"
                type="tel"
                value={values.customer_phone}
                onChange={set("customer_phone")}
                inputProps={{ "aria-label": "Phone" }}
              />
            </Field>
            <Field label="Email">
              <TextField
                fullWidth
                size="small"
                type="email"
                value={values.customer_email}
                onChange={set("customer_email")}
                inputProps={{ "aria-label": "Email" }}
              />
            </Field>
            <Field label="Preferred">
              <div className="flex flex-wrap gap-x-5 lg:flex-col lg:gap-0">
                {["Email", "Phone"].map((option) => (
                  <Choice
                    key={option}
                    name="preferred_contact"
                    label={option}
                    checked={values.preferred_contact === option}
                    onChange={() => setValue("preferred_contact", option)}
                  />
                ))}
              </div>
            </Field>
          </div>
        </Section>

        {/* Address */}
        <Section title="Address" required>
          <div className="space-y-4">
            <Field label="Street Address">
              <TextField
                fullWidth
                size="small"
                value={values.address}
                onChange={set("address")}
                inputProps={{ "aria-label": "Street Address" }}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="City">
                <TextField
                  fullWidth
                  size="small"
                  value={values.city}
                  onChange={set("city")}
                  inputProps={{ "aria-label": "City" }}
                />
              </Field>
              <Field label="State">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={values.state}
                  onChange={set("state")}
                  SelectProps={{
                    displayEmpty: true,
                    SelectDisplayProps: { "aria-label": "State" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select a state...
                  </MenuItem>
                  {leadStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field label="ZIP Code">
                <TextField
                  fullWidth
                  size="small"
                  value={values.zip}
                  onChange={set("zip")}
                  inputProps={{ "aria-label": "ZIP Code", inputMode: "numeric" }}
                />
              </Field>
            </div>
          </div>
        </Section>

        {/* Salesperson */}
        <Section title="Salesperson" description="Pre-filled with your details.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Salesperson Name" required>
              <TextField
                fullWidth
                size="small"
                value={values.salesperson_name}
                onChange={set("salesperson_name")}
                inputProps={{ "aria-label": "Salesperson Name" }}
              />
            </Field>
            <Field
              label="Salesperson Email"
              required
              hint="You'll be emailed a copy of the lead."
            >
              <TextField
                fullWidth
                size="small"
                type="email"
                value={values.salesperson_email}
                onChange={set("salesperson_email")}
                inputProps={{ "aria-label": "Salesperson Email" }}
              />
            </Field>
          </div>
        </Section>

        {/* Interests */}
        <Section title="Project">
          <div className="space-y-5">
            <Field label="Interested In a Quote on:">
              <div className="grid grid-cols-1 gap-2 min-[400px]:grid-cols-2 sm:grid-cols-3">
                <Check
                  label="Select All"
                  checked={allInterests}
                  onChange={toggleAllInterests}
                />
                {INTEREST_OPTIONS.map((option) => (
                  <Check
                    key={option}
                    label={option}
                    checked={values.interested_in.includes(option)}
                    onChange={() => toggleInterest(option)}
                  />
                ))}
              </div>
            </Field>

            <Field label="Notes (Optional)">
              <TextField
                fullWidth
                multiline
                minRows={5}
                value={values.notes}
                onChange={set("notes")}
                inputProps={{ "aria-label": "Notes (Optional)" }}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Do you have any specific concerns or issues with your home?">
                <TextField
                  fullWidth
                  size="small"
                  value={values.concerns}
                  onChange={set("concerns")}
                  inputProps={{
                    "aria-label":
                      "Do you have any specific concerns or issues with your home?",
                  }}
                />
              </Field>
              <Field label="Was your home built prior to 1978?">
                <div className="flex flex-wrap gap-x-5">
                  {["Yes", "No", "Unsure"].map((option) => (
                    <Choice
                      key={option}
                      name="built_prior_1978"
                      label={option}
                      checked={values.built_prior_1978 === option}
                      onChange={() => setValue("built_prior_1978", option)}
                    />
                  ))}
                </div>
              </Field>
            </div>
          </div>
        </Section>

        {error && (
          <div
            role="alert"
            className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300"
          >
            {error}
          </div>
        )}

        <div className="app-card flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-4">
          <Button
            type="button"
            variant="text"
            color="inherit"
            onClick={startAnother}
            disabled={submitting}
          >
            Clear form
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </form>
    </PageShell>
  );
};

export default RemodelForm;
